import React, { useCallback, useEffect, useState } from 'react'

import { setActiveFilterId, setDateRange, setSearchQuery } from 'slices/filters'

import { IconButton } from '@driftt/dds'
import { faFilter } from '@driftt/dds.icons/light/faFilter'
import { Div, Input, ToolTip } from '@driftt/tide-core'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import { DateRange, getDefaultRange } from 'components/DateRangePicker'
import NotificationSettings from 'components/NotificationSettings'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import CCRMFiltersCard from 'sections/CCRM/FiltersCard'

import styles from './styles.module.css'

interface Props {
  activeFilter: DeserializedSavedFilter | undefined
  onOpenEditModal: () => void
  setActiveFilterId: (id: number | undefined) => void
}

const CCRMHeaderBar = (props: Props) => {
  const { activeFilter } = props
  const [showFiltersCard, setShowFiltersCard] = useState(false)
  const dispatch = useAppDispatch()
  const { searchQuery, dateRange } = useAppSelector(({ filters }) => filters)

  const onDateRangeChange = useCallback(
    (dateRange: DateRange | undefined) => {
      dispatch(setDateRange(dateRange))
    },
    [dispatch]
  )

  const handleSetActiveFilterId = (id: number | undefined = -1) => {
    dispatch(setActiveFilterId(id))
  }

  useEffect(() => {
    const interval = setInterval(() => {
      // Clear date range, if present, and value input value is present
      if (dateRange && searchQuery) {
        onDateRangeChange(undefined)
      }
      // Reset default date range once inputs value is empty and dateRange is undefined
      if (!searchQuery && !dateRange) {
        onDateRangeChange(getDefaultRange())
      }
    }, 300)

    return () => clearInterval(interval)
  }, [onDateRangeChange, dateRange, dispatch, searchQuery])

  const onSearchChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
    dispatch(setSearchQuery(value))

  return (
    <Div className={styles.headerBarWrapper}>
      <Div className={styles.headerBarLeft}>
        <Div className={styles.headerBarTitle}>{activeFilter?.name || 'All accounts'}</Div>
        <Div className={styles.headerBarIcon}>
          <ToolTip flow="down" tethered render={() => 'Filters'}>
            <IconButton
              title="Filter"
              onClick={() => setShowFiltersCard(!showFiltersCard)}
              icon={faFilter}
            />
          </ToolTip>
        </Div>
        <Div className={styles.headerBarIcon}>
          <NotificationSettings />
        </Div>
      </Div>
      <Input
        className={styles.searchInput}
        type="search"
        placeholder="Search by account name..."
        size="large"
        defaultValue={searchQuery}
        onChange={onSearchChange}
      />
      {showFiltersCard && (
        <CCRMFiltersCard
          onClose={() => setShowFiltersCard(false)}
          onOpenEditModal={props.onOpenEditModal}
          setActiveFilterId={handleSetActiveFilterId}
          useDefaultStyles
        />
      )}
    </Div>
  )
}

export default CCRMHeaderBar
